// Create a matrix that tracks how many times each pair of players has played together
const createPlayerPairingMatrix = (players, previousRounds) => {
  const matrix = {};
  
  // Initialize the matrix for all player pairs
  players.forEach(player1 => {
    matrix[player1] = {};
    players.forEach(player2 => {
      if (player1 !== player2) {
        matrix[player1][player2] = 0;
      }
    });
  });
  
  // Count how many times each pair of players has been on the same team
  previousRounds.forEach(round => {
    round.forEach(team => {
      for (let i = 0; i < team.length; i++) {
        for (let j = i + 1; j < team.length; j++) {
          const player1 = team[i];
          const player2 = team[j];
          
          if (matrix[player1] && matrix[player1][player2] !== undefined) {
            matrix[player1][player2]++;
          }
          
          if (matrix[player2] && matrix[player2][player1] !== undefined) {
            matrix[player2][player1]++;
          }
        }
      }
    });
  });
  
  return matrix;
};

// Compute a per-player pressure score (how often they repeated with others)
const computePlayerPressure = (players, pairingMatrix) => {
  const pressure = new Map();
  players.forEach(p => {
    let sum = 0;
    const row = pairingMatrix[p] || {};
    Object.values(row).forEach(v => { sum += v; });
    pressure.set(p, sum);
  });
  return pressure;
};

// Find the best player to add to a team based on minimizing prior interactions
const findBestPlayerMatch = (currentTeam, availablePlayers, pairingMatrix) => {
  if (availablePlayers.length === 0) return null;
  if (currentTeam.length === 0) return availablePlayers[Math.floor(Math.random() * availablePlayers.length)];

  let bestPlayer = availablePlayers[0];
  let minPairingScore = Infinity;

  availablePlayers.forEach(player => {
    let pairingScore = 0;

    currentTeam.forEach(teamMember => {
      if (pairingMatrix[player] && pairingMatrix[player][teamMember] !== undefined) {
        pairingScore += pairingMatrix[player][teamMember];
      }
    });

    // Add significant randomness to break ties and avoid deterministic assignments
    // When pairing score is 0 (no history), this allows for 0-200% variance
    pairingScore = pairingScore + Math.random() * 2;

    if (pairingScore < minPairingScore) {
      minPairingScore = pairingScore;
      bestPlayer = player;
    }
  });

  return bestPlayer;
};

// Score an assignment: superlinear penalty on repeated teammate pairs
const scoreAssignment = (roundTeams, pairingMatrix) => {
  let score = 0;
  for (const team of roundTeams) {
    for (let i = 0; i < team.length; i++) {
      for (let j = i + 1; j < team.length; j++) {
        const a = team[i];
        const b = team[j];
        const c = (pairingMatrix[a]?.[b] ?? 0);
        // superlinear: square growth
        score += c * c;
      }
    }
  }
  return score;
};

// Build one assignment attempt using pressure-based seeding and greedy fill
const buildAssignmentAttempt = (players, numTeams, desiredSizes, pairingMatrix, pressureOrder) => {
  // Initialize teams
  const teams = Array.from({ length: numTeams }, () => []);
  const remaining = new Set(players);

  // Create a shuffled list of team indices to avoid always assigning to same positions
  const teamIndices = Array.from({ length: numTeams }, (_, i) => i);
  for (let i = teamIndices.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [teamIndices[i], teamIndices[j]] = [teamIndices[j], teamIndices[i]];
  }

  // Seed with highest-pressure players spread across teams (using shuffled indices)
  for (let t = 0; t < numTeams && t < pressureOrder.length; t++) {
    const p = pressureOrder[t];
    if (!remaining.has(p)) continue;
    teams[teamIndices[t]].push(p);
    remaining.delete(p);
  }
  // If some teams not seeded yet, seed with random remaining
  for (let t = 0; t < numTeams; t++) {
    if (teams[t].length === 0 && remaining.size > 0) {
      const arr = Array.from(remaining);
      const idx = Math.floor(Math.random() * arr.length);
      const p = arr[idx];
      teams[t].push(p);
      remaining.delete(p);
    }
  }
  
  // Fill teams up to desired sizes
  const availablePlayers = Array.from(remaining);
  // Shuffle available players to avoid order bias
  for (let i = availablePlayers.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [availablePlayers[i], availablePlayers[j]] = [availablePlayers[j], availablePlayers[i]];
  }
  // Iterate teams in round-robin to reduce bias, start at random position
  let ptr = Math.floor(Math.random() * numTeams);
  while (availablePlayers.length > 0) {
    const teamIndex = ptr % numTeams;
    ptr++;
    const target = desiredSizes[teamIndex] ?? 0;
    if (teams[teamIndex].length >= target) continue;
    const best = findBestPlayerMatch(teams[teamIndex], availablePlayers, pairingMatrix);
    if (best == null) break;
    teams[teamIndex].push(best);
    const k = availablePlayers.indexOf(best);
    if (k >= 0) availablePlayers.splice(k, 1);
  }
  
  // Distribute any leftover players (if desiredSizes underspecified)
  let idx = 0;
  while (availablePlayers.length > 0) {
    const teamIndex = idx % numTeams;
    teams[teamIndex].push(availablePlayers.shift());
    idx++;
  }
  
  return teams;
};

// Team Assignment Utility with optimized player variety (multi-start, pressure seeding)
export const assignPlayersToTeams = (players, numTeams, previousRounds = [], targetSizes = null) => {
  if (numTeams <= 0 || players.length === 0) {
    return [];
  }

  // Create a matrix to track how many times each pair of players have been on the same team
  const playerPairings = createPlayerPairingMatrix(players, previousRounds);
  const pressure = computePlayerPressure(players, playerPairings);
  const pressureOrder = players.slice().sort((a, b) => (pressure.get(b) || 0) - (pressure.get(a) || 0));
  
  // Determine desired team sizes
  let desiredSizes = [];
  if (Array.isArray(targetSizes) && targetSizes.length >= numTeams) {
    desiredSizes = targetSizes.slice(0, numTeams).map(v => Math.max(0, Math.floor(v)));
    // If sum < players, we'll fill remaining later round-robin to respect variety
  } else {
    const minPlayersPerTeam = Math.floor(players.length / numTeams);
    const extraPlayers = players.length % numTeams;
    desiredSizes = Array.from({ length: numTeams }, (_, i) => i < extraPlayers ? minPlayersPerTeam + 1 : minPlayersPerTeam);
  }
  
  // Multi-start attempts: build several candidates and pick best by score
  const ATTEMPTS = Math.min(200, Math.max(50, players.length * 10));
  let bestTeams = null;
  let bestScore = Infinity;
  for (let attempt = 0; attempt < ATTEMPTS; attempt++) {
    // Shuffle pressure order significantly for variety (30% swap probability)
    const noisyPressure = pressureOrder.slice();
    for (let i = noisyPressure.length - 1; i > 0; i--) {
      if (Math.random() < 0.3) {
        const j = Math.floor(Math.random() * (i + 1));
        [noisyPressure[i], noisyPressure[j]] = [noisyPressure[j], noisyPressure[i]];
      }
    }
    const candidate = buildAssignmentAttempt(players, numTeams, desiredSizes, playerPairings, noisyPressure);
    const s = scoreAssignment(candidate, playerPairings);
    if (s < bestScore) {
      bestScore = s;
      bestTeams = candidate;
    }
  }
  return bestTeams || [];
};